import { type Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { distinctUntilChanged, map, switchMap } from 'rxjs';

/** Minimal shape `resolveEnumLabel` needs from `TranslocoService` — keeps the helper unit-testable. */
export interface EnumLabelTranslator {
  translate(key: string): string;
}

/**
 * Resolves the translated label for an enum `value` under `prefix`.
 *
 * - null / undefined / empty value → empty string.
 * - Missing translation key (Transloco echoes the key back when unresolved) → raw `value`.
 * - Otherwise → the translated label.
 */
export function resolveEnumLabel(
  translator: EnumLabelTranslator | null,
  prefix: string,
  value: string | null | undefined,
): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const key = `${prefix}.${value}`;
  const translated = translator?.translate(key);
  return translated && translated !== key ? translated : value;
}

/**
 * Builds a signal that re-emits every time the i18n scope backing `prefix` finishes (re)loading
 * for the active language — the reactive trigger an OnPush enum label depends on so its
 * translation doesn't go stale after a runtime language switch.
 *
 * The scope is `prefix`'s first dot-segment (the bounded-context folder under `public/i18n/`,
 * e.g. `'production-planning.equipment.Type'` → scope `'production-planning'`).
 *
 * Deliberately uses `selectTranslation(scope)`, NOT `langChanges$`: per the transloco-i18n skill
 * ("Common Errors" table), `langChanges$` emits BEFORE the new language's JSON has loaded, so
 * reacting to it directly would recompute the label while the new translations don't exist yet —
 * permanently sticking it on the raw-value fallback. `selectTranslation` emits only once the
 * scope's translations for the new language are actually available.
 *
 * Must run inside an injection context (called from a component field initializer, or from a test
 * via `TestBed.runInInjectionContext`) — `toObservable`/`toSignal` require it.
 */
export function deriveLanguageLoadedSignal(
  translator: Pick<TranslocoService, 'selectTranslation'>,
  prefix: Signal<string>,
): Signal<unknown> {
  return toSignal(
    toObservable(prefix).pipe(
      map((value) => value.split('.')[0]),
      distinctUntilChanged(),
      switchMap((scope) => translator.selectTranslation(scope)),
    ),
    { initialValue: null },
  );
}
